import { MessageType } from 'api/MessageTypeAPI';
import {
  FREQUENCY_CAP_OPTIONS,
  MESSAGE_TYPE_FIELDS_TEXT,
  maxIntValue,
} from 'modules/channels/constants';
import { Controller } from 'react-hook-form';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { PrimaryButton, BUTTON_SIZES } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { RadioList } from 'ui/radioList';
import { HeaderWrapper, InputFieldWrapper, StyledDescription, StyledSuffix } from './Styled';
import { FIELDS, UPDATE_FREQUENCY_CAP_TEXT } from './constants';
import { FrequencyCapFormValues } from './types';
import { useUpdateFrequencyCapForm } from './useFrequencyCapForm';

const {
  VALUES: { CUSTOM },
} = MESSAGE_TYPE_FIELDS_TEXT;

export interface ModalProps {
  onClose: () => void;
  messageType: MessageType;
}

export const UpdateFrequencyCapModal = ({ onClose, messageType }: ModalProps) => {
  const {
    methods: {
      control,
      formState: { isSubmitted, isValid },
      watch,
    },
    onSubmit,
  } = useUpdateFrequencyCapForm({ messageType, onClose });

  const radioOption = watch(FIELDS.frequencyCapOption as keyof FrequencyCapFormValues);
  const isCustomFrequencyCap = radioOption === CUSTOM;

  return (
    <Modal
      id={UPDATE_FREQUENCY_CAP_TEXT.MODAL_ID}
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>
        <HeaderWrapper>{UPDATE_FREQUENCY_CAP_TEXT.HEADER}</HeaderWrapper>
      </ModalHeader>
      <ModalBody>
        <StyledDescription>{UPDATE_FREQUENCY_CAP_TEXT.DESCRIPTION}</StyledDescription>
        <Controller
          control={control}
          name={FIELDS.frequencyCapOption as keyof FrequencyCapFormValues}
          render={({ field: { onChange, value, name } }) => (
            <RadioList
              label=""
              name={name}
              onSelect={onChange}
              options={FREQUENCY_CAP_OPTIONS}
              value={value}
            />
          )}
        />
        <InputFieldWrapper>
          <Controller
            control={control}
            name={FIELDS.numMessagesInPeriod as keyof FrequencyCapFormValues}
            render={({ field: { onChange, onBlur, value = '', name }, fieldState: { error } }) => (
              <FormField errorText={error?.message} hasError={!!error && isCustomFrequencyCap}>
                <InputText
                  id={`${messageType.id}`}
                  type="number"
                  maxValue={maxIntValue}
                  minValue={1}
                  onBlur={onBlur}
                  onChange={onChange}
                  name={name}
                  value={`${value}`}
                  isDisabled={!isCustomFrequencyCap}
                  Suffix={<StyledSuffix>messages</StyledSuffix>}
                />
              </FormField>
            )}
          />
          <span>in a</span>
          <Controller
            control={control}
            name={FIELDS.period as keyof FrequencyCapFormValues}
            render={({ field: { onChange, onBlur, value = '', name }, fieldState: { error } }) => (
              <FormField errorText={error?.message} hasError={!!error && isCustomFrequencyCap}>
                <InputText
                  id={`${messageType.id}`}
                  type="number"
                  maxValue={maxIntValue}
                  minValue={1}
                  onBlur={onBlur}
                  onChange={onChange}
                  name={name}
                  value={`${value}`}
                  isDisabled={!isCustomFrequencyCap}
                  Suffix={<StyledSuffix>day</StyledSuffix>}
                />
              </FormField>
            )}
          />
          <span>period</span>
        </InputFieldWrapper>
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={onSubmit}
          isDisabled={isSubmitted && !isValid}
          size={BUTTON_SIZES.LARGE}
        >
          {UPDATE_FREQUENCY_CAP_TEXT.ACTION_BUTTON}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
